import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Import components (make sure paths are correct)
import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import Branch from "../components/Hamdan Exchange/ExchangeBranch";
import Contact from "../components/Hamdan Exchange/ExchangeContact";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const HamdanExchange: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/api/homepage/content');
        const data = await response.json();
        setContent(data.Exchange || {});
      } catch (err) {
        console.error('Exchange fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Get active tab from URL, default to "about"
  const activeTab = searchParams.get("tab") || "about";

  const menuItems = [
    { id: "about", label: t('common.about') },
    { id: "services", label: t('common.services') },
    { id: "branch", label: t('common.branches') },
    { id: "clients", label: t('common.clients') },
    { id: "contact", label: t('common.contact') },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  const heroBanners = [1, 2, 3]
    .map(i => ({ image: content?.[`banner_${i}`] }))
    .filter(banner => banner.image);
  const clientLogoImages = buildClientLogoImages(content);
  const clientLogoUrls = t_api(content?.clients_logo_urls, content?.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={{
        title: t_api(content?.banner_title, content?.banner_title_ar),
        subtitle: t_api(content?.banner_subtitle, content?.banner_subtitle_ar),
        banners: heroBanners.length
          ? heroBanners
          : [
            { image: "/banner/exchange_1.jpg" },
            { image: "/banner/exchange_2.jpg" },
            { image: "/banner/exchange_3.jpg" }
          ]
      }}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <DivisionIntro content={{
              kicker: "Hamdan Group - Oman",
              title: t_api(content?.about_title, content?.about_title_ar),
              desc: t_api(content?.about_desc, content?.about_desc_ar),
              image: content?.about_image || "/Hamdan-Exchange-logo.jpg",
              imageFit: "contain",
              imageBadgeTitle: "Exchange",
              imageLabel: t_api(content?.about_title, content?.about_title_ar) || "Hamdan Exchange",
              noteTitle: "Web Info",
              noteDesc: t_api(content?.about_web_info, content?.about_web_info_ar),
              panelTitle: "INTRODUCTION",
              capabilitiesTitle: "Exchange Services",
              capabilitiesMeta: "Foreign Exchange - Remittance - Branches",
              brandLogo: "/Hamdan-Exchange-logo.jpg",
              brandDesc: t_api(content?.about_web_info, content?.about_web_info_ar) || "A foreign exchange service division with multilingual staff and branch support across Oman.",
              brandTags: ["Exchange", "Remittance", "Oman"],
              points: [
                { title: t_api(content?.about_pt_1_title, content?.about_pt_1_title_ar), desc: t_api(content?.about_pt_1_desc, content?.about_pt_1_desc_ar), icon: content?.about_pt_1_icon },
                { title: t_api(content?.about_pt_2_title, content?.about_pt_2_title_ar), desc: t_api(content?.about_pt_2_desc, content?.about_pt_2_desc_ar), icon: content?.about_pt_2_icon },
                { title: t_api(content?.about_pt_3_title, content?.about_pt_3_title_ar), desc: t_api(content?.about_pt_3_desc, content?.about_pt_3_desc_ar), icon: content?.about_pt_3_icon }
              ]
            }} />
            <DivisionClientsStrip
              title={t_api(content?.clients_title, content?.clients_title_ar)}
              desc={t_api(content?.clients_desc, content?.clients_desc_ar)}
              clientsText={t_api(content?.clients_list, content?.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}

        {activeTab === "branch" && <Branch content={{
          branches: Array.from({ length: 40 }, (_, i) => i + 1)
            .map(i => ({
              name: t_api(content?.[`br_${i}_name`], content?.[`br_${i}_name_ar`]),
              phone: content?.[`br_${i}_phone`],
              fax: content?.[`br_${i}_fax`],
              address: t_api(content?.[`br_${i}_addr`], content?.[`br_${i}_addr_ar`]),
              coordinates: content?.[`br_${i}_coord`]
            }))

            .filter(b => b.name)
        }} />}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(content?.clients_title, content?.clients_title_ar),
          desc: t_api(content?.clients_desc, content?.clients_desc_ar),
          clientsText: t_api(content?.clients_list, content?.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Exchange",
          footer: t_api(content?.clients_footer, content?.clients_footer_ar),
          footerDesc: t_api(content?.clients_footer_desc, content?.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && <Contact content={{
          form_title: t_api(content?.contact_form_title, content?.contact_form_title_ar),
          info_title: t_api(content?.contact_info_title, content?.contact_info_title_ar),
          info_desc: t_api(content?.contact_info_desc, content?.contact_info_desc_ar),
          loc_name: t_api(content?.contact_loc_name, content?.contact_loc_name_ar),
          loc_addr: t_api(content?.contact_loc_addr, content?.contact_loc_addr_ar),
          loc_phone: content?.contact_loc_phone,
          loc_fax: content?.contact_loc_fax,
          loc_email: content?.contact_loc_email,
          muscat_name: t_api(content?.contact_muscat_name, content?.contact_muscat_name_ar),
          muscat_phone: content?.contact_muscat_phone,
          muscat_fax: content?.contact_muscat_fax

        }} />}
      </div>
    </GroupPageShell>
  );
};

export default HamdanExchange;
